import React from "react";
import authService, { KindergartenSettings } from "../services/authService.ts";
import { setOrgType } from "../i18n/orgLabel";
import {
  applySessionReplay,
  identifyUser,
  resetAnalytics,
  track,
} from "../services/analytics.ts";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { User } from "./types";
import {
  canAccessRoute,
  canManageJourneys,
  isOfficeStaff,
  isRoleBlockedBeforeSignIn,
  isSignedInUserBlocked,
} from "../utils/routeAccess";

// Whether this person's session is recorded is the backend's call (a root
// admin's percentage, or a request to record them — see analytics.ts
// applySessionReplay). Asked after sign-in and on session restore, at most
// every ten minutes per person: checkAuth runs on many route mounts.
const REPLAY_POLICY_TTL_MS = 10 * 60 * 1000;
let replayPolicyAskedFor: { userId: string; at: number } | null = null;

const syncSessionReplay = (userId: string): void => {
  const now = Date.now();
  if (
    replayPolicyAskedFor?.userId === userId &&
    now - replayPolicyAskedFor.at < REPLAY_POLICY_TTL_MS
  ) {
    return;
  }
  replayPolicyAskedFor = { userId, at: now };
  // Loaded on demand: the service's apiClient imports this store, and a cycle
  // that is harmless at call time is still not worth adding at import time.
  import("../services/systemSettingsService.ts")
    .then(({ systemSettingsService }) =>
      systemSettingsService.getSessionReplayPolicy("dashboard")
    )
    .then((policy) => applySessionReplay(policy?.sessions_percent ?? 0))
    // Fails closed, like the endpoint: an answer we could not get is "no".
    .catch(() => applySessionReplay(0));
};

export const Roles = {
  ROOT_ADMIN: "root_admin",
  DRIVER: "driver",
  PARENT: "parent",
  KINDERGARTEN_ADMIN: "kindergarten_admin",
  KINDERGARTEN_USER: "kindergarten_user",
  // Wosool staff who keep Customers from the app. A root-level account with no
  // school, refused by this dashboard after login (`isSignedInUserBlocked`):
  // agents work from the phone, and root admins see their records here.
  SALES_AGENT: "sales_agent",
  ACCOUNTANT: "accountant",
  // A school's teacher (Education), made and used in the Wosool app. Refused
  // by both login gates whatever the driver/parent switch says; here only to
  // be labelled where one appears. Never offered by the user forms.
  TEACHER: "teacher",
} as const;

/**
 * Capabilities a route may require on top of its `allowedRoles`.
 *
 * A closed union rather than a free string: a typo in a route definition
 * would otherwise silently grant the page to everyone the role list admits.
 */
export type RouteCapability = "manages_journeys" | "views_journeys";

// Types for the new multi-step login flow
interface KindergartenOption {
  kindergarten_id: string;
  kindergarten_name: string;
  role: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  kindergartenSettings: KindergartenSettings | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | { message: string; status?: number; retryAfter?: number } | null;
  kindergartenOptions: KindergartenOption[];
  checkPhone: (
    phone: string
  ) => Promise<{ isRoot: boolean; kindergartens: KindergartenOption[] }>;
  login: (
    phone: string,
    password: string,
    kindergartenId?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isBillingEnabled: () => boolean;
  /** Whether the signed-in user may author this school's journeys. */
  canManageJourneys: () => boolean;
  /**
   * Whether the signed-in user is school office staff. Gates the navbar's
   * office tools, all of which call staff-only endpoints.
   */
  isOfficeStaff: () => boolean;
  /**
   * Whether the signed-in user may reach a route. The ONE predicate the
   * sidebar and the route guard both ask, so a menu item can never offer a
   * page the guard then refuses.
   */
  canAccessRoute: (route: {
    allowedRoles?: string[];
    requiresCapability?: RouteCapability;
  }) => boolean;
  isRoleBlocked: (role: string) => boolean;
  /** The post-login gate. See `isRoleBlocked` for why there are two. */
  isSignedInUserBlocked: () => boolean;
  /** Merge into persisted kindergarten settings (e.g. after settings save or kg switch). */
  patchKindergartenSettings: (partial: Partial<KindergartenSettings>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      kindergartenSettings: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      kindergartenOptions: [],

      checkPhone: async (phone: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.authService.checkPhone(phone);
          const kindergartens = response.kindergartens || [];
          set({
            kindergartenOptions: kindergartens,
            isLoading: false,
          });
          return {
            isRoot: response.is_root,
            kindergartens: kindergartens,
          };
        } catch (error: any) {
          // Store error message and preserve rate limit info if available
          const errorMessage = error.message || "An error occurred";
          const errorData: any = { message: errorMessage };
          
          // Preserve rate limit information (status 429)
          if (error.publicStatus === 429) {
            errorData.status = 429;
            errorData.retryAfter = (error as any).retryAfter || 60;
          }
          
          set({ error: errorData, isLoading: false });
          throw error;
        }
      },

      login: async (
        phone: string,
        password: string,
        kindergartenId?: string
      ) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.authService.login(
            phone,
            password,
            kindergartenId
          );
          set({
            token: response.access_token,
            user: response.user,
            kindergartenSettings: response.kindergarten_settings,
            isAuthenticated: true,
          });
          // Keep the org-label resolver in sync with the tenant we logged into.
          setOrgType(response.kindergarten_settings?.management_type);

          // Identify before tracking so the event lands on the right profile.
          identifyUser(response.user, response.kindergarten_settings?.name);
          syncSessionReplay(response.user.id);
          track("login_completed", {
            login_method: "phone_password",
            role: response.user.role,
            kindergarten_id: response.user.kindergarten_id || null,
            // Root admins pick a school at login; everyone else is scoped to one.
            selected_kindergarten: Boolean(kindergartenId),
            billing_enabled: Boolean(
              response.kindergarten_settings?.billing_enabled
            ),
          });
        } catch (error: any) {
          // Store error message and preserve rate limit info if available
          const errorMessage = error.message || "An error occurred";
          const errorData: any = { message: errorMessage };
          
          // Preserve rate limit information (status 429)
          if (error.publicStatus === 429) {
            errorData.status = 429;
            errorData.retryAfter = (error as any).retryAfter || 60;
          }
          
          set({ error: errorData });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        try {
          // Call logout endpoint for server-side cleanup (FCM token revocation, token version increment)
          await authService.authService.logout();
        } catch (error) {
          // Log error but continue with client-side cleanup
          // This ensures logout always works even if endpoint fails
          console.error("Logout endpoint error:", error);
        } finally {
          // Drop the Mixpanel identity too — dashboards get shared, and
          // without this the next admin on this browser inherits the last
          // one's distinct_id. (It stops any recording as well, and the next
          // person's is asked for afresh.)
          resetAnalytics();
          replayPolicyAskedFor = null;
          // Always clear client state
          set({
            token: null,
            user: null,
            kindergartenSettings: null,
            isAuthenticated: false,
            kindergartenOptions: [],
          });
        }
      },

      checkAuth: async () => {
        try {
          const user = await authService.authService.getMe();
          set({ user });
          // Re-bind identity on session restore (refresh / new tab) so events
          // after a rehydrate still attach to this admin.
          identifyUser(user, get().kindergartenSettings?.name);
          syncSessionReplay(user.id);
          return true;
        } catch (error) {
          get().logout();
          return false;
        }
      },

      hasRole: (role: string) => {
        const user = get().user;
        if (!user) return false;
        return user.role === role;
      },

      hasAnyRole: (roles: string[]) => {
        const user = get().user;
        if (!user) return false;
        return roles.includes(user.role);
      },

      isBillingEnabled: () => {
        const settings = get().kindergartenSettings;
        if (!settings) return false;
        return settings.billing_enabled === true;
      },

      // The rules themselves live in `utils/routeAccess.js` as pure functions,
      // so the sidebar, the route guard and `npm run test:unit` all exercise
      // one implementation. These are just the signed-in-user bindings.
      canAccessRoute: (route) => canAccessRoute(get().user, route),

      canManageJourneys: () => canManageJourneys(get().user),

      isOfficeStaff: () => isOfficeStaff(get().user),

      /**
       * The PRE-login gate, answered from a role alone.
       *
       * `check_phone_number` is anonymous: it returns a role and a school for
       * any number typed into the form, and nothing more. It deliberately does
       * NOT say whether a driver manages journeys — publishing "this number
       * belongs to a journey manager at school X" to unauthenticated callers
       * buys nothing.
       *
       * So a driver is let through to the password step and decided
       * afterwards by `isSignedInUserBlocked`. A parent is still refused here,
       * where it saves them typing a password for a door that will not open,
       * and a teacher always is. The rule is `isRoleBlockedBeforeSignIn`.
       */
      isRoleBlocked: (role: string) =>
        // The environment variable is read directly to avoid a circular
        // dependency.
        isRoleBlockedBeforeSignIn(
          role,
          process.env.REACT_APP_ENABLE_DRIVER_PARENT_ACCESS === "true"
        ),

      /**
       * The POST-login gate, and the real one — by now the user document is in
       * hand, so a journey manager can be told apart from an ordinary
       * supervisor.
       */
      isSignedInUserBlocked: () =>
        isSignedInUserBlocked(
          get().user,
          process.env.REACT_APP_ENABLE_DRIVER_PARENT_ACCESS === "true"
        ),

      patchKindergartenSettings: (partial: Partial<KindergartenSettings>) => {
        const current = get().kindergartenSettings;
        if (!current) {
          return;
        }
        const next = { ...current, ...partial };
        set({ kindergartenSettings: next });
        // Reflect a management_type change (settings save) immediately.
        if (partial.management_type !== undefined) {
          setOrgType(next.management_type);
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      // This part ensures all necessary auth data is saved.
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        kindergartenSettings: state.kindergartenSettings,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ----------------------------------
// 3) Optional: A specialized protected route that checks roles
// ----------------------------------

interface RoleProtectedRouteProps {
  requiredRoles: string[];
  children: React.ReactNode;
}

export const RoleProtectedRoute = ({
  requiredRoles,
  children,
}: RoleProtectedRouteProps) => {
  const { isAuthenticated, checkAuth, user } = useAuthStore();
  const navigate = useNavigate();

  React.useEffect(() => {
    const verifyAuthAndRoles = async () => {
      // If not authenticated, attempt checking or navigate away
      const isAuth = await checkAuth();
      if (!isAuth) {
        navigate("/auth/login");
        return;
      }

      // If no role match, navigate away or show Not Authorized
      if (!requiredRoles.includes(user?.role || "")) {
        navigate("/not-authorized");
        return;
      }
    };

    verifyAuthAndRoles();
  }, [isAuthenticated, checkAuth, user, navigate, requiredRoles]);

  if (!isAuthenticated) {
    // Could show a loading spinner or nothing at all
    return null;
  }

  // If the user is authenticated and has the required role(s), allow access
  return children;
};
